// frontend/app/exhibit/v2/screens/TeachMachineScreen.js

import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";

import {
  TEACH_QUEUE,
  createTeachModel,
  teachLoss,
  teachProbability,
  trainTeachModel,
} from "../models/teachClassifier";
import { tx } from "../i18n/exhibitLocale";

const BLUE = "#3E7FBF";
const RED = "#E0566E";

const COPY = {
  h1: {
    el: "Teach it, one shape at a time.",
    hs: "Teach it, one shape at a time.",
  },
  sub: {
    el: "Say what each shape is. The machine guesses first, then learns from your answer. Watch it get better, and watch it get tricked.",
    ms: "Label each shape. The machine guesses first, then learns from your label. Watch it improve, and watch where it goes wrong.",
    hs: "Label each shape. The machine guesses first, then learns from your label. Watch it sharpen — and watch where it goes wrong.",
  },
  none: {
    el: "Nothing yet. Label the first shape.",
    hs: "Nothing yet — label the first shape.",
  },
  drawerOpen: {
    el: "Want to see the numbers inside? ▸",
    hs: "Curious what turns inside? ▸",
  },
  drawerClose: {
    el: "Hide the numbers ▾",
    hs: "Hide the numbers ▾",
  },
  guessing: {
    el: "“Just guessing.” It has no examples yet.",
    hs: "“Just guessing.” It has no examples yet.",
  },
  score: {
    el: "Machine guesses matched your labels: ",
    hs: "Machine guesses matched your labels: ",
  },
  scoreOf: {
    el: " of ",
    hs: " of ",
  },
  scoreEnd: {
    el: " guesses so far.",
    hs: " guesses so far.",
  },
  fbFirst: {
    el: "It was only guessing. Now it has its first example.",
    hs: "It was guessing. Now it has its first example.",
  },
  fbFew: {
    el: "Wrong, but it had only {n} example to go on. Every answer you give it counts.",
    hs: "Wrong — but it had only {n} example to go on. Every label counts.",
  },
  fbRight: {
    el: "Right! It has {n} examples now.",
    hs: "Right. {n} examples now.",
  },
  fbFooled: {
    el: "Tricked! Every circle it saw was blue, so it learned “blue = circle.” Your answer just told it that is wrong.",
    ms: "Fooled! Every circle it saw was blue, so it learned “blue = circle.” Your label just told it otherwise.",
    hs: "Fooled. Every circle it had seen was blue, so it learned “blue = circle.” Your label just told it otherwise.",
  },
  fbReverse: {
    el: "Tricked again, the other way! It thought red means triangle. It was using color, not shape.",
    ms: "Fooled again, the other way: it thought red meant triangle. Color was its shortcut, not shape.",
    hs: "Fooled again, the other way: it thought red meant triangle. Color was its shortcut, not shape.",
  },
  fbWrong: {
    el: "Wrong. But now it knows. Each fix nudges its numbers.",
    hs: "Wrong — and now it knows. Each correction nudges its numbers.",
  },
  fbTest: {
    el: "It got the blue triangle right this time! Your fixes broke the shortcut.",
    ms: "It got the blue triangle this time. Your corrections broke the shortcut.",
    hs: "It got the blue triangle this time. Your corrections broke the shortcut.",
  },
  fbMislabel: {
    el: " (That one is really a {truth}. The machine learns whatever you tell it!)",
    hs: " (That one is actually a {truth}. The machine learns whatever you tell it.)",
  },
  endH1: {
    el: "You trained it!",
    hs: "You trained it.",
  },
  endText: {
    el: "You gave it 12 examples. The first six taught it a shortcut: blue means circle. Shape 7 broke it. Your fixes on shapes 7 to 10 taught it to look at the shape. By shape 11 it got the blue triangle right. More kinds of examples mean fewer shortcuts. But never zero.",
    ms: "You gave it 12 examples. The first six taught it a shortcut: blue means circle. Shape 7 broke it. Your corrections on shapes 7 to 10 taught it to look at shape, not color. By shape 11 it got the blue triangle right. More kinds of examples reduce shortcuts. They don’t remove them.",
    hs: "You gave it 12 examples. The first six taught it a shortcut: blue means circle. Shape 7 broke it. Your corrections on shapes 7–10 taught it to weigh shape over color, and by shape 11 it got the blue triangle right. More kinds of examples reduce shortcuts — they don't remove them.",
  },
  endBadLabels: {
    el: "You gave the machine {n} label that did not match the shape. It learned that label anyway. That is important: a machine learns from what people tell it, even when the training data is wrong.",
    ms: "You gave the machine {n} incorrect label. It learned from that label anyway. Training data quality matters: wrong labels can teach the model the wrong rule.",
    hs: "You gave the model {n} incorrect label. It learned from it anyway. That is the point: models optimize against the training labels they receive, so bad labels can teach bad rules.",
  },
  endBadLabelsPlural: {
    el: "You gave the machine {n} labels that did not match the shapes. It learned those labels anyway. That is important: a machine learns from what people tell it, even when the training data is wrong.",
    ms: "You gave the machine {n} incorrect labels. It learned from them anyway. Training data quality matters: wrong labels can teach the model the wrong rule.",
    hs: "You gave the model {n} incorrect labels. It learned from them anyway. That is the point: models optimize against the training labels they receive, so bad labels can teach bad rules.",
  },
  drawerNote: {
    el: "Each answer: guess → compare with your label → see how wrong → nudge the numbers. Big AI systems nudge millions of numbers.",
    ms: "Each label: predict → compare with the label → measure the error → nudge the numbers. Real systems adjust millions of these.",
    hs: "Each label: predict → compare with the label → measure error → nudge the numbers. Real systems adjust millions of these.",
  },
};

function pickCopy(entry, tier, language = "en") {
  if (!entry) return "";
  return tx(language, entry[tier] ?? entry.hs ?? entry.el ?? "");
}

function fill(template, values) {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ""));
}

function Shape({ example, size = 170 }) {
  const isCircle = example.r >= 0.5;
  const backgroundColor = example.b >= 0.5 ? BLUE : RED;

  if (isCircle) {
    return (
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor,
        }}
      />
    );
  }

  return (
    <View
      style={{
        width: 0,
        height: 0,
        borderLeftWidth: size / 2,
        borderRightWidth: size / 2,
        borderBottomWidth: size,
        borderLeftColor: "transparent",
        borderRightColor: "transparent",
        borderBottomColor: backgroundColor,
      }}
    />
  );
}

function ExampleChip({ example, tier, language = "en" }) {
  return (
    <View style={styles.exampleWrap}>
      <View style={styles.exampleBox}>
        <Shape example={example} size={42} />
      </View>
      <Text style={styles.exampleLabel}>
        {tx(language, example.y ? "CIRCLE" : "TRIANGLE")}
      </Text>
    </View>
  );
}

export default function TeachMachineScreen({
  tier = "hs",
  language = "en",
  onBack = () => {},
  onNarrationMoment = () => {},
}) {
  const [examples, setExamples] = useState([]);
  const [model, setModel] = useState(() => createTeachModel());
  const [index, setIndex] = useState(0);
  const [machineRight, setMachineRight] = useState(0);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const current = TEACH_QUEUE[index] ?? null;
  const isFinished = index >= TEACH_QUEUE.length;

  const currentProbability = useMemo(() => {
    if (!current) return 0.5;
    return teachProbability(model, current);
  }, [model, current]);

  const loss = useMemo(
    () => teachLoss(model, examples),
    [model, examples]
  );

  const mislabelCount = useMemo(
    () => examples.reduce((count, example, exampleIndex) => {
      const truth = TEACH_QUEUE[exampleIndex]?.y;
      return count + (truth != null && example.y !== truth ? 1 : 0);
    }, 0),
    [examples]
  );

  const reset = () => {
    setExamples([]);
    setModel(createTeachModel());
    setIndex(0);
    setMachineRight(0);
    setLastFeedback(null);
    setDrawerOpen(false);
  };

  const labelShape = (visitorLabel) => {
    if (!current) return;

    const hadExamples = examples.length > 0;
    const probability = teachProbability(model, current);
    const guess = hadExamples ? (probability >= 0.5 ? 1 : 0) : null;
    const wasRight = guess !== null && guess === visitorLabel;

    if (wasRight) {
      setMachineRight((value) => value + 1);
    }

    const n = examples.length;
    let text;

    if (guess === null) {
      text = pickCopy(COPY.fbFirst, tier, language);
    } else if (wasRight && current.test && visitorLabel === current.y) {
      text = pickCopy(COPY.fbTest, tier, language);
    } else if (wasRight) {
      text = fill(pickCopy(COPY.fbRight, tier, language), { n: n + 1 });
    } else if (current.surprise && visitorLabel === 0) {
      text = pickCopy(COPY.fbFooled, tier, language);
    } else if (current.reverse && visitorLabel === 1) {
      text = pickCopy(COPY.fbReverse, tier, language);
    } else if (n < 3) {
      text = fill(pickCopy(COPY.fbFew, tier, language), { n });
    } else {
      text = pickCopy(COPY.fbWrong, tier, language);
    }

    if (visitorLabel !== current.y) {
      text += fill(pickCopy(COPY.fbMislabel, tier, language), {
        truth: tx(language, current.y ? "circle" : "triangle"),
      });
    }

    const nextExamples = [
      ...examples,
      {
        b: current.b,
        r: current.r,
        y: visitorLabel,
      },
    ];

    const nextModel = trainTeachModel(model, nextExamples);

    setLastFeedback({
      text,
      right: wasRight,
      guess,
    });
    setExamples(nextExamples);
    setModel(nextModel);
    if (index === TEACH_QUEUE.length - 1) {
      onNarrationMoment("v2_teach_takeaway");
    }
    setIndex((value) => value + 1);
  };

  const pct = Math.round(currentProbability * 100);
  const confidence = Math.max(pct, 100 - pct);
  const predictedCircle = pct >= 50;

  return (
    <View style={styles.container}>
      <View style={styles.topBar}>
        <Pressable onPress={onBack} style={styles.backButton}>
          <Text style={styles.backText}>{tx(language, "← Menu")}</Text>
        </Pressable>
        <Text style={styles.brand}>{tx(language, "Teach a Machine")}</Text>
      </View>

      <View style={styles.main}>
        <View style={styles.leftColumn}>
          <Text style={styles.heading}>{pickCopy(COPY.h1, tier, language)}</Text>
          <Text style={styles.sub}>{pickCopy(COPY.sub, tier, language)}</Text>

          <Text style={styles.eyebrow}>{tx(language, "WHAT YOU HAVE TAUGHT IT")}</Text>

          <View style={styles.examplesRow}>
            {examples.length === 0 ? (
              <Text style={styles.emptyText}>{pickCopy(COPY.none, tier, language)}</Text>
            ) : (
              examples.map((example, exampleIndex) => (
                <ExampleChip
                  key={`${exampleIndex}-${example.b}-${example.r}`}
                  example={example}
                  tier={tier}
                  language={language}
                />
              ))
            )}
          </View>

          <Pressable
            onPress={() => setDrawerOpen((value) => !value)}
            style={styles.drawerToggle}
          >
            <Text style={styles.drawerToggleText}>
              {drawerOpen
                ? pickCopy(COPY.drawerClose, tier, language)
                : pickCopy(COPY.drawerOpen, tier, language)}
            </Text>
          </Pressable>

          {drawerOpen && (
            <View style={styles.drawer}>
              <Text style={styles.drawerText}>
                {tx(language, "Examples: ")}<Text style={styles.bold}>{examples.length}</Text>
                {"  ·  "}{tx(language, "Error (loss): ")}<Text style={styles.bold}>{loss.toFixed(3)}</Text>
              </Text>
              <Text style={styles.drawerText}>
                {tx(language, "Weight for blue: ")}<Text style={styles.bold}>{model.wBlue.toFixed(2)}</Text>
                {"  ·  "}{tx(language, "Weight for round: ")}<Text style={styles.bold}>{model.wRound.toFixed(2)}</Text>
                {"  ·  "}{tx(language, "Bias: ")}<Text style={styles.bold}>{model.bias.toFixed(2)}</Text>
              </Text>
              <Text style={styles.drawerNote}>
                {pickCopy(COPY.drawerNote, tier, language)}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.rightColumn}>
          {!isFinished ? (
            <>
              <Text style={styles.cap}>
                {language === "es" ? `FIGURA ${index + 1} DE ${TEACH_QUEUE.length}` : `SHAPE ${index + 1} OF ${TEACH_QUEUE.length}`}
              </Text>

              <View style={styles.feedbackArea}>
                {lastFeedback ? (
                  <Text
                    style={[
                      styles.feedback,
                      lastFeedback.guess === null
                        ? styles.feedbackMuted
                        : lastFeedback.right
                        ? styles.feedbackGood
                        : styles.feedbackWarn,
                    ]}
                  >
                    {lastFeedback.text}
                  </Text>
                ) : null}
              </View>

              <View style={styles.shapeBox}>
                <Shape example={current} size={170} />
              </View>

              <Text style={styles.guessLabel}>{tx(language, "THE MACHINE GUESSES")}</Text>

              {examples.length === 0 ? (
                <Text style={styles.guessText}>
                  {pickCopy(COPY.guessing, tier, language)}
                </Text>
              ) : (
                <Text style={styles.guessText}>
                  <Text style={styles.accent}>
                    “{tx(language, predictedCircle ? "Circle" : "Triangle")}”
                  </Text>
                  {language === "es" ? ` — puntuación del modelo ${confidence}%` : ` — model score ${confidence}%`}
                </Text>
              )}

              {examples.length > 0 && (
                <View style={styles.meterWrap}>
                  <View style={styles.meterLabels}>
                    <Text style={styles.meterCircle}>{tx(language, "Circle")} {pct}%</Text>
                    <Text style={styles.meterTriangle}>{tx(language, "Triangle")} {100 - pct}%</Text>
                  </View>
                  <View style={styles.track}>
                    <View style={[styles.fill, { width: `${pct}%` }]} />
                  </View>
                </View>
              )}

              <Text style={styles.ask}>{tx(language, "What is it?")}</Text>

              <View style={styles.buttonRow}>
                <Pressable
                  onPress={() => labelShape(1)}
                  style={({ pressed }) => [
                    styles.answerButton,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text style={styles.answerText}>{tx(language, "It's a circle")}</Text>
                </Pressable>

                <Pressable
                  onPress={() => labelShape(0)}
                  style={({ pressed }) => [
                    styles.answerButton,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text style={styles.answerText}>{tx(language, "It's a triangle")}</Text>
                </Pressable>
              </View>

              {examples.length > 1 && (
                <Text style={styles.score}>
                  {pickCopy(COPY.score, tier, language)}
                  {machineRight}
                  {pickCopy(COPY.scoreOf, tier, language)}
                  {examples.length - 1}
                  {pickCopy(COPY.scoreEnd, tier, language)}
                </Text>
              )}
            </>
          ) : (
            <View style={styles.endWrap}>
              <View style={styles.endIconBox}>
                <Text style={styles.endIcon}>🎓</Text>
              </View>

              <Text style={styles.endHeading}>{pickCopy(COPY.endH1, tier, language)}</Text>

              <Text style={styles.endScore}>
                {language === "es"
                  ? tier === "el"
                    ? `Etiquetaste ${TEACH_QUEUE.length} figuras. Después de aprender de la primera, la máquina hizo ${TEACH_QUEUE.length - 1} predicciones y acertó ${machineRight}.`
                    : `Etiquetaste ${TEACH_QUEUE.length} figuras. Después del primer ejemplo, la máquina hizo ${TEACH_QUEUE.length - 1} predicciones puntuadas y acertó ${machineRight}.`
                  : tier === "el"
                  ? `You labeled ${TEACH_QUEUE.length} shapes. After learning from the first one, the machine made ${TEACH_QUEUE.length - 1} guesses and matched ${machineRight} of them.`
                  : `You labeled ${TEACH_QUEUE.length} shapes. After the first example, the machine made ${TEACH_QUEUE.length - 1} scored guesses and matched ${machineRight} of them.`}
              </Text>

              <Text style={styles.endText}>
                {mislabelCount > 0
                  ? fill(
                      pickCopy(
                        mislabelCount === 1
                          ? COPY.endBadLabels
                          : COPY.endBadLabelsPlural,
                        tier,
                        language
                      ),
                      { n: mislabelCount }
                    )
                  : pickCopy(COPY.endText, tier, language)}
              </Text>

              <View style={styles.endButtons}>
                <Pressable onPress={onBack} style={styles.answerButton}>
                  <Text style={styles.answerText}>{tx(language, "Back to the menu →")}</Text>
                </Pressable>

                <Pressable onPress={reset} style={styles.secondaryButton}>
                  <Text style={styles.secondaryButtonText}>{tx(language, "Teach it again")}</Text>
                </Pressable>
              </View>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 1920,
    height: 1080,
    backgroundColor: "#070D17",
  },
  topBar: {
    height: 78,
    paddingHorizontal: 56,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    paddingVertical: 10,
    paddingRight: 26,
  },
  backText: {
    fontSize: 19,
    fontWeight: "800",
    color: "#8FA5BC",
  },
  brand: {
    fontSize: 19,
    fontWeight: "800",
    color: "#8FA5BC",
    letterSpacing: 0.8,
  },
  main: {
    flex: 1,
    flexDirection: "row",
    paddingHorizontal: 130,
    paddingBottom: 50,
    gap: 70,
    alignItems: "center",
  },
  leftColumn: {
    flex: 1,
    minWidth: 0,
  },
  rightColumn: {
    width: 720,
    alignItems: "center",
  },
  heading: {
    fontSize: 60,
    lineHeight: 66,
    fontWeight: "900",
    color: "#F4F8FC",
  },
  sub: {
    marginTop: 16,
    maxWidth: 600,
    fontSize: 23,
    lineHeight: 34,
    color: "#B9C8D8",
  },
  eyebrow: {
    marginTop: 36,
    fontSize: 15,
    fontWeight: "900",
    color: "#5F7690",
    letterSpacing: 2.2,
  },
  examplesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 14,
    minHeight: 110,
    maxWidth: 640,
    alignItems: "flex-start",
  },
  emptyText: {
    fontSize: 20,
    color: "#8FA5BC",
  },
  exampleWrap: {
    alignItems: "center",
  },
  exampleBox: {
    width: 72,
    height: 72,
    borderRadius: 16,
    backgroundColor: "#0D1626",
    borderWidth: 1,
    borderColor: "#1B2A44",
    alignItems: "center",
    justifyContent: "center",
  },
  exampleLabel: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "800",
    letterSpacing: 0.7,
    color: "#8FA5BC",
  },
  drawerToggle: {
    marginTop: 28,
    alignSelf: "flex-start",
    paddingVertical: 8,
  },
  drawerToggleText: {
    fontSize: 19,
    fontWeight: "800",
    color: "#8FA5BC",
  },
  drawer: {
    marginTop: 12,
    maxWidth: 620,
    paddingHorizontal: 22,
    paddingVertical: 18,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#1B2A44",
    backgroundColor: "#0D1626",
  },
  drawerText: {
    fontSize: 18,
    lineHeight: 28,
    color: "#B9C8D8",
  },
  drawerNote: {
    marginTop: 8,
    fontSize: 15,
    lineHeight: 22,
    color: "#7F94AA",
  },
  bold: {
    fontWeight: "900",
    color: "#F4F8FC",
  },
  cap: {
    fontSize: 17,
    fontWeight: "800",
    letterSpacing: 2,
    color: "#8FA5BC",
  },
  feedbackArea: {
    minHeight: 74,
    marginTop: 10,
    maxWidth: 680,
    justifyContent: "center",
  },
  feedback: {
    textAlign: "center",
    fontSize: 22,
    lineHeight: 31,
    fontWeight: "800",
  },
  feedbackMuted: {
    color: "#8FA5BC",
  },
  feedbackGood: {
    color: "#63D6A0",
  },
  feedbackWarn: {
    color: "#F0B45A",
  },
  shapeBox: {
    width: 260,
    height: 260,
    borderRadius: 44,
    marginTop: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0D1626",
    borderWidth: 1,
    borderColor: "#1B2A44",
    shadowColor: "#4696DC",
    shadowOpacity: 0.18,
    shadowRadius: 70,
    shadowOffset: { width: 0, height: 24 },
  },
  guessLabel: {
    marginTop: 22,
    fontSize: 19,
    fontWeight: "800",
    letterSpacing: 2,
    color: "#8FA5BC",
  },
  guessText: {
    marginTop: 6,
    minHeight: 44,
    fontSize: 34,
    lineHeight: 42,
    fontWeight: "900",
    color: "#F4F8FC",
    textAlign: "center",
  },
  accent: {
    color: "#4FD1FF",
  },
  meterWrap: {
    width: 500,
    marginTop: 14,
  },
  meterLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  meterCircle: {
    color: "#4FD1FF",
    fontSize: 16,
    fontWeight: "800",
  },
  meterTriangle: {
    color: "#5F7690",
    fontSize: 16,
    fontWeight: "800",
  },
  track: {
    width: "100%",
    height: 14,
    borderRadius: 999,
    overflow: "hidden",
    backgroundColor: "#16243A",
  },
  fill: {
    height: "100%",
    backgroundColor: "#4FD1FF",
  },
  ask: {
    marginTop: 26,
    fontSize: 24,
    fontWeight: "800",
    color: "#F4F8FC",
  },
  buttonRow: {
    flexDirection: "row",
    gap: 22,
    marginTop: 14,
  },
  answerButton: {
    minWidth: 210,
    paddingHorizontal: 28,
    paddingVertical: 18,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0E3149",
    borderWidth: 2,
    borderColor: "#4FD1FF",
  },
  answerText: {
    fontSize: 21,
    fontWeight: "900",
    color: "#F4F8FC",
  },
  pressed: {
    opacity: 0.88,
  },
  score: {
    marginTop: 18,
    fontSize: 18,
    color: "#8FA5BC",
  },
  endWrap: {
    width: "100%",
    alignItems: "center",
  },
  endIconBox: {
    width: 260,
    height: 260,
    borderRadius: 44,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0D1626",
    borderWidth: 1,
    borderColor: "#1B2A44",
  },
  endIcon: {
    fontSize: 110,
  },
  endHeading: {
    marginTop: 10,
    fontSize: 52,
    fontWeight: "900",
    color: "#F4F8FC",
  },
  endScore: {
    marginTop: 10,
    fontSize: 26,
    fontWeight: "700",
    color: "#F4F8FC",
  },
  endText: {
    marginTop: 16,
    maxWidth: 680,
    fontSize: 22,
    lineHeight: 33,
    color: "#B9C8D8",
    textAlign: "center",
  },
  endButtons: {
    marginTop: 24,
    flexDirection: "row",
    gap: 18,
  },
  secondaryButton: {
    minWidth: 190,
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0D1626",
    borderWidth: 1,
    borderColor: "#1B2A44",
  },
  secondaryButtonText: {
    fontSize: 20,
    fontWeight: "800",
    color: "#B9C8D8",
  },
});
